import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { StatCard } from '@/components/pm/StatCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { POLLING_INTERVALS } from '@/constants/polling';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import { extractApiErrorMessage } from '@/lib/api-error';
import { formatStatusLabel } from '@/lib/utils';
import { getOrgDashboard, listOrgProperties } from '@/services/api.client';

export function OrgAdminHomePage() {
  const ctx = useOrgContext();
  const dashboardInterval = useVisibilityPolling(POLLING_INTERVALS.ORG_ADMIN.DASHBOARD);

  const dashboardQuery = useQuery({
    queryKey: ['org-dashboard', ctx?.organisationId],
    queryFn: () => getOrgDashboard(ctx?.organisationId ?? ''),
    enabled: !!ctx,
    refetchInterval: dashboardInterval,
  });

  const propertiesQuery = useQuery({
    queryKey: ['org-properties', ctx?.organisationId],
    queryFn: () => listOrgProperties(ctx?.organisationId ?? ''),
    enabled: !!ctx,
    staleTime: 5 * 60 * 1000,
  });

  const { data: dashboard, isLoading: dashLoading } = dashboardQuery;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl">Organisation Dashboard</h1>
        {dashboard?.organisationName && (
          <p className="text-sm text-muted-foreground mt-0.5">{dashboard.organisationName}</p>
        )}
      </div>

      {dashboardQuery.isError ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-[var(--border-error)] bg-[var(--status-red-tint)] p-3 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-[var(--status-red-text)]" />
          <span className="text-[var(--text-secondary)]">
            Couldn&apos;t load organisation stats.{' '}
            {extractApiErrorMessage(dashboardQuery.error, '')}
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Today's Visits"
            value={dashboard?.todaysVisits ?? 0}
            loading={dashLoading}
          />
          <StatCard
            label="Open Incidents"
            value={dashboard?.openIncidents ?? 0}
            alert={(dashboard?.openIncidents ?? 0) > 0}
            loading={dashLoading}
          />
          <StatCard
            label="Guards On Duty"
            value={dashboard?.totalGuards ?? 0}
            loading={dashLoading}
          />
          <StatCard
            label="Active Properties"
            value={dashboard?.activeProperties ?? 0}
            loading={dashLoading}
          />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium">Properties</h2>
          <Link to="/org/properties" className="text-sm text-primary hover:underline">
            View all
          </Link>
        </div>

        <QueryStateBoundary
          query={propertiesQuery}
          errorTitle="Couldn't load properties"
          errorFallback="The properties list is unavailable. Try again in a moment."
          loadingFallback={
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-36 rounded-xl" />
              ))}
            </div>
          }
          emptyFallback={
            <p className="text-sm text-muted-foreground">
              No properties yet.{' '}
              <Link to="/org/properties" className="text-primary hover:underline">
                Create one.
              </Link>
            </p>
          }
        >
          {(properties) => (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* F1 fix: property cards match Figma — per-property stats + PM dashboard link */}
              {properties.map((p) => (
                <div
                  key={p.id}
                  className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-sm text-[var(--text-primary)]">{p.name}</p>
                    <Badge
                      variant={p.status === 'active' ? 'default' : 'outline'}
                      className="text-xs"
                    >
                      {formatStatusLabel(p.status)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
                    <span className="truncate" title={p.address}>
                      {p.address}
                    </span>
                    <span>
                      Registration:{' '}
                      <span className="text-[var(--text-primary)]">
                        {p.registrationMode === 'self_registration' ? 'Self-reg' : 'Invite only'}
                      </span>
                    </span>
                    <span className="truncate">
                      Subdomain:{' '}
                      <span className="font-mono text-[var(--text-primary)]">{p.subdomain}</span>
                    </span>
                    <span>
                      Type: <span className="capitalize">{p.propertyType.replace(/_/g, ' ')}</span>
                    </span>
                  </div>
                  <Button variant="outline" size="sm" asChild className="self-end">
                    <Link to={`/manager?propertyId=${p.id}`}>
                      Open PM Dashboard <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </QueryStateBoundary>
      </div>
    </div>
  );
}
